/* src/components/customGame/setup/panels/GlobalSettingsPanel.js */
import React from 'react';
import PropTypes from 'prop-types';
import Accordion from '../../ui/Accordion'; // Assuming you have or will create this

// A helper for consistent section styling if needed
const SettingsSection = ({ title, children }) => (
    <div className="mb-6 p-4 border border-gray-200 rounded-md shadow-sm bg-white">
        <h3 className="text-lg font-medium text-gray-800 mb-3">{title}</h3>
        <div className="space-y-4">
            {children}
        </div>
    </div>
);

SettingsSection.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};


const GlobalSettingsPanel = ({ config, onConfigChange, pathPrefix }) => {

    const handleChange = (key, value) => {
        // Prepend the pathPrefix to the key before calling onConfigChange
        onConfigChange(`${pathPrefix}.${key}`, value);
    };

    // Specific handler for nested NValue properties
    const handleNValueChange = (subKey, value) => {
        handleChange(`NValue.${subKey}`, value);
    };

    // Specific handler for nested Termination properties
    const handleTerminationChange = (subKey, value) => {
        handleChange(`Termination.${subKey}`, value);
    };

    // Specific handler for nested RandomSeed properties
    const handleRandomSeedChange = (subKey, value) => {
        handleChange(`RandomSeed.${subKey}`, value);
    };
    
    // Specific handler for nested AdaptiveSettings properties
    const handleAdaptiveSettingChange = (subKey, value) => {
        // Ensure numeric values are stored as numbers
        const numericValue = ['initialN', 'increaseThreshold', 'decreaseThreshold'].includes(subKey) ? parseFloat(value) : value;
        handleChange(`NValue.AdaptiveSettings.${subKey}`, numericValue);
    };


    // Ensure config and its nested properties exist to prevent errors
    const nValueConfig = config?.NValue || {};
    const terminationConfig = config?.Termination || {};
    const randomSeedConfig = config?.RandomSeed || {};
    const adaptiveSettingsConfig = nValueConfig?.AdaptiveSettings || {};


    return (
        <div className="p-6 space-y-6 bg-gray-50 min-h-full"> {/* Added min-h-full and bg-gray-50 */}
            <SettingsSection title="N-Back Level (Load Factor 'n')">
                <div>
                    <label htmlFor="nValueMode" className="block text-sm font-medium text-gray-700 mb-1">
                        N-Value Mode
                    </label>
                    <select
                        id="nValueMode"
                        name="nValueMode"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={nValueConfig.Mode || 'Fixed'}
                        onChange={(e) => handleNValueChange('Mode', e.target.value)}
                    >
                        <option value="Fixed">Fixed N-Value</option>
                        <option value="Adaptive (Standard)">Adaptive (Standard)</option>
                        <option value="Manual Per Stimulus">Manual Per Stimulus (Advanced)</option>
                    </select>
                </div>

                {nValueConfig.Mode === 'Fixed' && (
                    <div>
                        <label htmlFor="fixedNValue" className="block text-sm font-medium text-gray-700 mb-1">
                            Fixed N-Value
                        </label>
                        <input
                            type="number"
                            id="fixedNValue"
                            name="fixedNValue"
                            className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={nValueConfig.FixedValue || 2}
                            onChange={(e) => handleNValueChange('FixedValue', parseInt(e.target.value, 10))}
                            min="1"
                            max="12"
                        />
                    </div>
                )}

                {nValueConfig.Mode === 'Adaptive (Standard)' && (
                     <Accordion title="Adaptive Settings" defaultOpen={false} id="adaptive-settings-accordion">
                        <div className="space-y-3 p-3 bg-gray-50 rounded-b-md border border-t-0 border-gray-200">
                            <div>
                                <label htmlFor="adaptiveInitialN" className="block text-sm font-medium text-gray-700 mb-1">
                                    Initial N
                                </label>
                                <input
                                    type="number"
                                    id="adaptiveInitialN"
                                    name="adaptiveInitialN"
                                    className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    value={adaptiveSettingsConfig.initialN || 1}
                                    onChange={(e) => handleAdaptiveSettingChange('initialN', e.target.value)}
                                    min="1"
                                    max="5" // Sensible max for initial N
                                />
                            </div>
                            <div>
                                <label htmlFor="adaptiveIncreaseThreshold" className="block text-sm font-medium text-gray-700 mb-1">
                                    Increase N Threshold (e.g., 0.85 for &gt;85% accuracy)
                                </label>
                                <input
                                    type="number"
                                    id="adaptiveIncreaseThreshold"
                                    name="adaptiveIncreaseThreshold"
                                    className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    value={adaptiveSettingsConfig.increaseThreshold || 0.85}
                                    onChange={(e) => handleAdaptiveSettingChange('increaseThreshold', e.target.value)}
                                    min="0.1"
                                    max="1.0"
                                    step="0.01"
                                />
                            </div>
                            <div>
                                <label htmlFor="adaptiveDecreaseThreshold" className="block text-sm font-medium text-gray-700 mb-1">
                                    Decrease N Threshold (e.g., 0.70 for &lt;70% accuracy)
                                </label>
                                <input
                                    type="number"
                                    id="adaptiveDecreaseThreshold"
                                    name="adaptiveDecreaseThreshold"
                                    className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    value={adaptiveSettingsConfig.decreaseThreshold || 0.70}
                                    onChange={(e) => handleAdaptiveSettingChange('decreaseThreshold', e.target.value)}
                                    min="0.0"
                                    max="0.9"
                                    step="0.01"
                                />
                            </div>
                            {/* Add more adaptive settings here if needed, like min/max N for adaptive mode */}
                        </div>
                    </Accordion>
                )}
                
                {nValueConfig.Mode === 'Manual Per Stimulus' && (
                     <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-700">
                        <p>Configuration for 'Manual Per Stimulus' N-values will be available within each enabled stimulus type's settings panel.</p>
                    </div>
                )}

            </SettingsSection>

            <SettingsSection title="Session Termination">
                <div>
                    <label htmlFor="terminationMode" className="block text-sm font-medium text-gray-700 mb-1">
                        Termination Mode
                    </label>
                    <select
                        id="terminationMode"
                        name="terminationMode"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={terminationConfig.Mode || 'Fixed Trial Count'}
                        onChange={(e) => handleTerminationChange('Mode', e.target.value)}
                    >
                        <option value="Fixed Trial Count">Fixed Trial Count</option>
                        <option value="Fixed Duration">Fixed Duration (seconds)</option>
                    </select>
                </div>

                {terminationConfig.Mode === 'Fixed Trial Count' && (
                    <div>
                        <label htmlFor="trialCount" className="block text-sm font-medium text-gray-700 mb-1">
                            Total Trials
                        </label>
                        <input
                            type="number"
                            id="trialCount"
                            name="trialCount"
                            className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={terminationConfig.TrialCount || 60}
                            onChange={(e) => handleTerminationChange('TrialCount', parseInt(e.target.value, 10))}
                            min="10"
                            max="500"
                            step="1"
                        />
                    </div>
                )}
                {terminationConfig.Mode === 'Fixed Duration' && (
                    <div>
                        <label htmlFor="durationSeconds" className="block text-sm font-medium text-gray-700 mb-1">
                            Session Duration (seconds)
                        </label>
                        <input
                            type="number"
                            id="durationSeconds"
                            name="durationSeconds"
                            className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={terminationConfig.DurationSeconds || 180}
                            onChange={(e) => handleTerminationChange('DurationSeconds', parseInt(e.target.value, 10))}
                            min="30"      // Minimum 30 seconds
                            max="3600"    // Maximum 1 hour
                            step="10"
                        />
                    </div>
                )}
            </SettingsSection>

            <SettingsSection title="Random Seed">
                 <div>
                    <label htmlFor="randomSeedMode" className="block text-sm font-medium text-gray-700 mb-1">
                        Sequence Randomization
                    </label>
                    <select
                        id="randomSeedMode"
                        name="randomSeedMode"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={randomSeedConfig.Mode || 'Automatic'}
                        onChange={(e) => handleRandomSeedChange('Mode', e.target.value)}
                    >
                        <option value="Automatic">Automatic (Different each time)</option>
                        <option value="User Defined">User Defined (Reproducible sequence)</option>
                    </select>
                </div>
                {randomSeedConfig.Mode === 'User Defined' && (
                    <div>
                        <label htmlFor="randomSeedValue" className="block text-sm font-medium text-gray-700 mb-1">
                            Seed Value (Text or Number)
                        </label>
                        <input
                            type="text"
                            id="randomSeedValue"
                            name="randomSeedValue"
                            className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={randomSeedConfig.Value || 'dualnback'}
                            onChange={(e) => handleRandomSeedChange('Value', e.target.value)}
                            placeholder="Enter seed value"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            Using the same seed value will generate the exact same sequence of stimuli (if all other sequence-affecting parameters are also identical and the sequence generator version hasn't changed).
                        </p>
                    </div>
                )}
            </SettingsSection>
        </div>
    );
};

GlobalSettingsPanel.propTypes = {
    config: PropTypes.shape({
        NValue: PropTypes.shape({
            Mode: PropTypes.string,
            FixedValue: PropTypes.number,
            PerStimulusValue: PropTypes.object, // Will be more defined later
            AdaptiveSettings: PropTypes.shape({
                initialN: PropTypes.number,
                increaseThreshold: PropTypes.number,
                decreaseThreshold: PropTypes.number,
            }),
        }),
        Termination: PropTypes.shape({
            Mode: PropTypes.string,
            TrialCount: PropTypes.number,
            DurationSeconds: PropTypes.number,
        }),
        RandomSeed: PropTypes.shape({
            Mode: PropTypes.string,
            Value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
    }).isRequired,
    onConfigChange: PropTypes.func.isRequired,
    pathPrefix: PropTypes.string.isRequired, // e.g., "Session"
};

export default GlobalSettingsPanel;